import {
  deleteDocument,
  fetchDocuments,
  fetchNotes,
  postDocument,
  postNote,
} from './psCoreService';

/**
 * Thunks over the propstream-core contact endpoints: call, then put the result
 * in the store. The component reads it back through selectors, so nothing about
 * whether the section has loaded lives in local component state.
 *
 * Every request settles the `loaded` flag in a finally, so a failure shows an
 * empty section rather than leaving it spinning.
 */
export const LOAD_CONTACT_NOTES = 'LOAD_CONTACT_NOTES';
export const LOAD_CONTACT_NOTES_DONE = 'LOAD_CONTACT_NOTES_DONE';
export const SAVE_CONTACT_NOTE = 'SAVE_CONTACT_NOTE';
export const SAVE_CONTACT_NOTE_DONE = 'SAVE_CONTACT_NOTE_DONE';
export const LOAD_CONTACT_DOCUMENTS = 'LOAD_CONTACT_DOCUMENTS';
export const LOAD_CONTACT_DOCUMENTS_DONE = 'LOAD_CONTACT_DOCUMENTS_DONE';
export const UPLOAD_CONTACT_DOCUMENT = 'UPLOAD_CONTACT_DOCUMENT';
export const UPLOAD_CONTACT_DOCUMENT_DONE = 'UPLOAD_CONTACT_DOCUMENT_DONE';
export const DELETE_CONTACT_DOCUMENT = 'DELETE_CONTACT_DOCUMENT';
export const DELETE_CONTACT_DOCUMENT_DONE = 'DELETE_CONTACT_DOCUMENT_DONE';
export const RESET_CONTACT_EXTRAS = 'RESET_CONTACT_EXTRAS';

// The service keeps ONE editable note per contact (ATAI-114 product decision),
// even though the endpoint is plural and answers with a list.
const firstNote = (list) => (list && list.length ? list[0].note || '' : '');

// ATAI-388: loads/saves carry their contactId so the reducer can drop a response
// for a contact that is no longer open. Ids may be numbers or strings.
export const sameContactId = (a, b) => a != null && b != null && String(a) === String(b);

export const resetContactExtras = () => ({ type: RESET_CONTACT_EXTRAS });

// `prefix` ('contacts' | 'investors') passes straight through to
// psCoreService.js's contactUrl -- see its own comment. Omit it (the
// homeowner Contact Details editor does) to get the 'contacts' default.
export const loadContactNotes = (contactId, prefix) => async (dispatch) => {
  dispatch({ type: LOAD_CONTACT_NOTES, contactId });
  try {
    const { data } = await fetchNotes(contactId, prefix);
    dispatch({ type: LOAD_CONTACT_NOTES_DONE, contactId, note: firstNote(data) });
  } catch (error) {
    console.warn('Could not load contact notes:', error);
    dispatch({ type: LOAD_CONTACT_NOTES_DONE, contactId, note: '' });
  }
};

export const saveContactNote = (contactId, note, prefix) => async (dispatch) => {
  dispatch({ type: SAVE_CONTACT_NOTE });
  try {
    const { data } = await postNote(contactId, note, prefix);
    dispatch({ type: SAVE_CONTACT_NOTE_DONE, contactId, note: firstNote(data) });
  } catch (error) {
    console.warn('Could not save contact note:', error);
    // keep what the user typed rather than discarding it on a failed save
    dispatch({ type: SAVE_CONTACT_NOTE_DONE, contactId, note });
  }
};

export const loadContactDocuments = (contactId) => async (dispatch) => {
  dispatch({ type: LOAD_CONTACT_DOCUMENTS, contactId });
  try {
    const { data } = await fetchDocuments(contactId);
    dispatch({ type: LOAD_CONTACT_DOCUMENTS_DONE, contactId, documents: data || [] });
  } catch (error) {
    console.warn('Could not load contact documents:', error);
    dispatch({ type: LOAD_CONTACT_DOCUMENTS_DONE, contactId, documents: [] });
  }
};

export const uploadContactDocument = (contactId, file) => async (dispatch) => {
  dispatch({ type: UPLOAD_CONTACT_DOCUMENT });
  try {
    const { data } = await postDocument(contactId, file);
    dispatch({ type: UPLOAD_CONTACT_DOCUMENT_DONE, documents: data || [] });
  } catch (error) {
    console.warn('Could not upload contact document:', error);
    dispatch({ type: UPLOAD_CONTACT_DOCUMENT_DONE });
  }
};

// DELETE answers with the refreshed list, exactly as POST does, so the store is
// replaced from the response rather than by splicing the row out locally. The
// service soft-deletes (stamps delete_date/deleted_by) and 404s on a row that is
// already gone -- on that failure the list is simply left alone.
export const deleteContactDocument = (contactId, documentId) => async (dispatch) => {
  dispatch({ type: DELETE_CONTACT_DOCUMENT });
  try {
    const { data } = await deleteDocument(contactId, documentId);
    dispatch({ type: DELETE_CONTACT_DOCUMENT_DONE, documents: data || [] });
  } catch (error) {
    console.warn('Could not delete contact document:', error);
    dispatch({ type: DELETE_CONTACT_DOCUMENT_DONE });
  }
};
